import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import Pagination from 'components/Pagination';
import { AssetUrls } from 'config/assets';
import { SEOHelmet, StructuredData, BreadcrumbList } from 'components/SEO';
import { useGetPublishedArticlesQuery } from 'store/services/blog/blogApi';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { BlogArticle, BlogArticleType } from 'types/interfaces/blog';
import {
  ARTICLES_ERROR_COPY,
  ARTICLES_PAGE_SIZE,
  articlesPageTitle,
  buildArticlesSearch,
  formatArticlesCount,
  isNonCanonicalPageParam,
  isNonCanonicalTypeParam,
  pageHref,
  parseArticlesPage,
  parseArticlesQuery,
  parseArticlesType,
} from './articlesHubUrlState';
import './ArticlesHub.css';

const ARTICLES_CANONICAL = 'https://trademagic.gg/articles';
const SEARCH_DEBOUNCE_MS = 300;

interface PinnedArticle {
  title: string;
  description: string;
  readTime: string;
  hero: string;
  route: string;
  alt: string;
  buttonText?: string;
}

const PINNED_ARTICLES: PinnedArticle[] = [
  {
    title: 'How TradeMagic Works',
    description: 'A complete quick-start guide with everything you need to know about what makes TradeMagic special.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/login/login-background.png',
    route: '/articles/how-it-works',
    alt: 'TradeMagic background with magical cards'
  },
  {
    title: 'Shipping Guide',
    description: 'Instructions for how to mail Magic cards safely and easily.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.shippingGuide,
    route: '/articles/shipping',
    alt: 'Blue-themed wizard character with shipping supplies',
    buttonText: 'Read'
  },
  {
    title: 'Sustainable Economics',
    description: 'How we manage the long-term health of TradeMagic.',
    readTime: '3 MINS',
    hero: 'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/articles/hero_sensei_divining_top.png',
    route: '/articles/sustainable-economics',
    alt: 'Sensei\'s Divining Top card representing economic management',
    buttonText: 'Read'
  },
  {
    title: 'Condition Guide',
    description: 'Instructions for how to evaluate the condition of Magic cards.',
    readTime: '3 MINS',
    hero: AssetUrls.articles.conditionGuide,
    route: '/articles/condition-guide',
    alt: 'Futuristic crystalline city representing card quality standards',
    buttonText: 'Read'
  }
];

interface CmsArticleCard {
  title: string;
  description: string;
  hero: string;
  route: string;
  alt: string;
  articleType: BlogArticleType;
}

const toCard = (article: BlogArticle): CmsArticleCard => ({
  title: article.title,
  description: article.subtitle || '',
  hero: article.heroImageUrl || '',
  route: `/articles/${article.slug}`,
  alt: article.title,
  articleType: article.articleType || 'BLOG',
});

const matchesText = (article: BlogArticle, needle: string): boolean => {
  const n = needle.toLowerCase();
  return (
    article.title.toLowerCase().includes(n) ||
    (article.subtitle || '').toLowerCase().includes(n)
  );
};

const ArticlesHub = () => {
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // ---- URL state: page, q, type. Derived during render, never in an effect, so a
  // cold load of /articles?type=guides&page=2 paints the right list on the first frame.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawPage = searchParams.get('page');
  const rawType = searchParams.get('type');
  const page = parseArticlesPage(rawPage);
  const q = parseArticlesQuery(searchParams.get('q'));
  const activeType = parseArticlesType(rawType);

  // The input owns a draft; the URL owns the committed query. Clearing skips the debounce.
  const [draft, setDraft] = useState(q);
  const debouncedDraft = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS);
  const effectiveQuery = draft.trim() === '' ? '' : debouncedDraft.trim();
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const qRef = useRef(q);
  qRef.current = q;

  // Typing commits the debounced draft to the URL (replace, so keystrokes do not
  // build a back-button trail) and resets to page 1.
  useEffect(() => {
    if (effectiveQuery === qRef.current) return;
    setSearchParams(
      (prev) => new URLSearchParams(buildArticlesSearch(prev, { q: effectiveQuery, page: 1 })),
      { replace: true }
    );
  }, [effectiveQuery, setSearchParams]);

  // Back/forward or a shared link changed q underneath the input: adopt it. Skipped
  // while the draft already matches, so a trailing space mid-typing is not eaten.
  useEffect(() => {
    if (q !== draftRef.current.trim()) setDraft(q);
  }, [q]);

  const isSearching = draft.trim().length > 0 || q.length > 0;

  // Fetch the current window. `search` spans both types, so articleType is omitted
  // while a query is active (the tab row is hidden then, as before).
  const { data, currentData, isError, isFetching, isLoading } = useGetPublishedArticlesQuery({
    page,
    limit: ARTICLES_PAGE_SIZE,
    search: q || undefined,
    articleType: q ? undefined : activeType,
    sortBy: 'publishedAt',
    sortOrder: 'desc',
  });

  // `data` is the last successful result for ANY args (keeps the grid populated while
  // the next page loads); `currentData` matches the current args and is the only safe
  // input for the out-of-range check below.
  const serverPaging = typeof data?.pagination?.totalPages === 'number';
  const currentTotalPages = currentData?.pagination?.totalPages;

  // Normalize the URL: page=1 / page=0 / page=abc, a non-canonical type spelling, or a
  // page past the end all collapse to their canonical form with a replace, so the
  // reader sees page 1 with no error and no extra history entry.
  useEffect(() => {
    const patch: { page?: number; type?: BlogArticleType } = {};
    if (isNonCanonicalPageParam(rawPage)) patch.page = page;
    if (isNonCanonicalTypeParam(rawType)) patch.type = activeType;
    if (typeof currentTotalPages === 'number' && page > Math.max(currentTotalPages, 1)) patch.page = 1;
    if (Object.keys(patch).length === 0) return;
    setSearchParams((prev) => new URLSearchParams(buildArticlesSearch(prev, patch)), { replace: true });
  }, [rawPage, rawType, page, activeType, currentTotalPages, setSearchParams]);

  const setTab = useCallback(
    (next: BlogArticleType) => {
      if (next === activeType) return;
      // Push, so back returns to the previous tab; page resets with the tab.
      setSearchParams((prev) => new URLSearchParams(buildArticlesSearch(prev, { type: next, page: 1 })));
    },
    [activeType, setSearchParams]
  );

  // Scroll the list top into view when the page changes (not on typing or first paint).
  const listTopRef = useRef<HTMLDivElement | null>(null);
  const previousPageRef = useRef(page);
  useEffect(() => {
    if (previousPageRef.current !== page) {
      previousPageRef.current = page;
      listTopRef.current?.scrollIntoView({ block: 'start' });
    }
  }, [page]);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Rows to render. With server paging the response IS the window. Without it (an
  // older backend that ignores the params) fall back to filtering what arrived.
  const rows: BlogArticle[] = useMemo(() => {
    const received = data?.data ?? [];
    if (serverPaging) return received;
    return received.filter((article) => {
      if (!isSearching && (article.articleType || 'BLOG') !== activeType) return false;
      const needle = draft.trim();
      return needle ? matchesText(article, needle) : true;
    });
  }, [data, serverPaging, isSearching, activeType, draft]);

  const cards = useMemo(() => rows.map(toCard), [rows]);
  const total = serverPaging && data?.pagination ? data.pagination.totalArticles : rows.length;
  const totalPages = serverPaging && data?.pagination ? data.pagination.totalPages : 1;
  const showPager = serverPaging && totalPages > 1 && page <= totalPages;

  const emptyCopy = isSearching
    ? `No articles found for “${q || draft.trim()}”`
    : activeType === 'GUIDE'
      ? 'No guides yet.'
      : 'No blog posts yet.';

  return (
    <>
      {/* SEO Meta Tags. Canonical stays the bare /articles on every page and tab: the
          crawler index at /api/v1/meta/articles lists every article on one page, so
          page N has no separate canonical. Search result pages are noindex. */}
      <SEOHelmet
        title={articlesPageTitle(isSearching ? 1 : page)}
        description="Learn how to trade Magic cards on TradeMagic. Guides on card conditions, shipping, and sustainable trading economics."
        canonical={ARTICLES_CANONICAL}
        keywords="MTG trading guides, Magic card condition guide, MTG shipping guide, trade Magic cards, TradeMagic resources"
        type="website"
        noindex={isSearching}
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Articles', url: ARTICLES_CANONICAL },
        ]}
      />

      {/* CollectionPage Schema */}
      <StructuredData
        data={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Helpful Articles",
          "description": "Your HQ for TradeMagic resources",
          "url": ARTICLES_CANONICAL,
          "publisher": {
            "@type": "Organization",
            "name": "TradeMagic",
            "url": "https://trademagic.gg"
          }
        }}
      />

      <div className="articles-hub-layout">

      {/* Mobile Menu Overlay */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="articles-hub-mobile-overlay"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      <div className="articles-hub-content-wrapper">
        <LeftNavigationBar
          currentTab={{ path: '/articles', Component: <></> }}
          isPublicMode={!currentUser}
          onNavigationAttempt={handleNavigationAttempt}
          isAuthenticated={!!currentUser}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onMenuClose={() => setIsMobileMenuOpen(false)}
        />

        <div className="articles-hub-main">
          {/* Top Navigation Bar */}
          <TopNavigationBar
            title="Articles"
            isMobileMenuOpen={isMobileMenuOpen}
            onToggleMobileMenu={toggleMobileMenu}
          />

          <div className="articles-hub-container">
            {/* Page Header */}
            <header className="articles-hub-header">
              <h1 className="articles-hub-title">Helpful Articles</h1>
              <h2 className="articles-hub-subtitle">Your HQ for TradeMagic resources</h2>
            </header>

            {/* Pinned Articles Section */}
            <section className="articles-hub-pinned-section">
              <h3 className="articles-hub-section-title">Essential Guides</h3>
              <div className="articles-hub-pinned-grid">
                {PINNED_ARTICLES.map((article) => (
                  <Link
                    key={article.route}
                    to={article.route}
                    className="articles-hub-card articles-hub-pinned-card"
                  >
                    <div
                      className="articles-hub-card-hero"
                      style={{ backgroundImage: `url(${article.hero})` }}
                    >
                      <div className="articles-hub-card-overlay" />
                      <img
                        src={article.hero}
                        alt={article.alt}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <div className="articles-hub-card-content">
                      <div className="articles-hub-card-header">
                        <h3 className="articles-hub-card-title">{article.title}</h3>
                        {article.readTime && (
                          <span className="articles-hub-card-read-time">{article.readTime}</span>
                        )}
                      </div>
                      <p className="articles-hub-card-description">{article.description}</p>
                      <span className="articles-hub-card-button">{article.buttonText || 'Learn More'}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Search Bar */}
            <div className="articles-hub-search">
              <div className="articles-hub-search-wrapper">
                <svg className="articles-hub-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  type="text"
                  className="articles-hub-search-input"
                  placeholder="Search articles..."
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  aria-label="Search articles"
                />
                {draft && (
                  <button
                    className="articles-hub-search-clear"
                    onClick={() => setDraft('')}
                    aria-label="Clear search"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>

            <div ref={listTopRef} className="articles-hub-results">
              {/* Tab Row (hidden when searching; search spans both types) */}
              {!isSearching && (
                <div className="articles-hub-tabs">
                  <button
                    className={`articles-hub-tab ${activeType === 'BLOG' ? 'active' : ''}`}
                    onClick={() => setTab('BLOG')}
                    aria-pressed={activeType === 'BLOG'}
                  >
                    Blog
                  </button>
                  <button
                    className={`articles-hub-tab ${activeType === 'GUIDE' ? 'active' : ''}`}
                    onClick={() => setTab('GUIDE')}
                    aria-pressed={activeType === 'GUIDE'}
                  >
                    Guides
                  </button>
                </div>
              )}

              {/* Count line: the size of the current set, from the server total */}
              {!isError && !isLoading && (
                <p className="articles-hub-count" aria-live="polite">
                  {formatArticlesCount(total, isSearching ? q : '')}
                </p>
              )}

              {isError ? (
                <div className="articles-hub-empty" role="alert">{ARTICLES_ERROR_COPY}</div>
              ) : cards.length > 0 ? (
                <section className="articles-hub-grid" aria-busy={isFetching}>
                  {cards.map((article) => (
                    <Link
                      key={article.route}
                      to={article.route}
                      className="articles-hub-card"
                    >
                      <div
                        className="articles-hub-card-hero"
                        style={article.hero ? { backgroundImage: `url(${article.hero})` } : undefined}
                      >
                        <div className="articles-hub-card-overlay" />
                        {isSearching && (
                          <span className="articles-hub-type-label">
                            {article.articleType === 'BLOG' ? 'Blog' : 'Guide'}
                          </span>
                        )}
                        {/* Hidden img carries alt text for crawlers; an article without a
                            hero renders no img at all (src="" makes React warn and the
                            browser re-request the page). */}
                        {article.hero && (
                          <img
                            src={article.hero}
                            alt={article.alt}
                            style={{ display: 'none' }}
                          />
                        )}
                      </div>
                      <div className="articles-hub-card-content">
                        <div className="articles-hub-card-header">
                          <h3 className="articles-hub-card-title">{article.title}</h3>
                        </div>
                        <p className="articles-hub-card-description">{article.description}</p>
                        <span className="articles-hub-card-button">Read</span>
                      </div>
                    </Link>
                  ))}
                </section>
              ) : isLoading ? null : (
                <div className="articles-hub-empty">{emptyCopy}</div>
              )}

              {/* Pager: real anchors, so crawlers, shared links and the back button all work */}
              {showPager && (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  getPageHref={(p) => pageHref(searchParams, p)}
                  showFirstLast={false}
                  className="articles-hub-pager"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        collectionOwnerUsername=""
      />

      <Footer />
      </div>
    </>
  );
};

export default ArticlesHub;
